import React, { FunctionComponent } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { useApi } from 'components/providers/ApiProvider'
import { useParkingLot } from 'components/views/parking-lot/ParkingLotProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { useTranslation } from 'react-i18next'
import { TextField } from 'components/elements/TextField'

export interface DialogSession {
  id: number
  amountUnpaid: number
  amountPaid: number
}

type Props = {
  open: boolean
  session: DialogSession | null
}
type Functions = {
  setOpen(value: boolean): void
}
type AllProps = Props & Functions

const EditTotalAmountDialog: FunctionComponent<AllProps> = ({ open, setOpen, session }) => {
  if (!session) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { setLoadList } = useParkingLot()
  const { showSuccess, showError } = useMessage()

  const handleEnter = () => {
    formal.change('amount', ((session.amountUnpaid + session.amountPaid) / 100).toFixed(2))
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    formal.change('amount', initialValues.amount)
    formal.clearErrors()
  }

  const initialValues = {
    amount: 0,
  }

  const schema = yup.object().shape({
    amount: yup.number().required().min(0).test({
      test: value => {
        return (Math.round(value * 100) / 100) == value
      },
    }),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.updateParkingLotSessionTotalAmount({
        session_id: session.id,
        amount: Math.round(values.amount * 100),
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()

          setLoadList()

          showSuccess(t('messages.totalAmountChanged'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotChangeTotalAmount')} ${message}`)
        })
    },
  })

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose} TransitionProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('editTotalAmount')}</DraggableDialogTitle>
        <DialogContent>
          <Grid container>
            <TextField
              GridProps={{ xs: 12 }}
              label={t('totalAmount')}
              required
              {...formal.getFieldProps('amount')}
              autoComplete="off"
              autoFocus={true}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formal.isSubmitting}
          >
            {t('edit')}
          </Button>
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default EditTotalAmountDialog
